import React, { useEffect, useMemo, useState } from "react";
import { FloppyDisk, ArrowCounterClockwise, X, Plus } from "@phosphor-icons/react";
import { api } from "../../lib/api";
import { toast } from "sonner";

// Section labels for tabs
const TABS = [
  { key: "global", label: "Global" },
  { key: "home", label: "Home" },
  { key: "services_page", label: "Services Page" },
  { key: "gallery_page", label: "Gallery" },
  { key: "blog_page", label: "Journal" },
  { key: "contact_page", label: "Contact" },
  { key: "service_detail", label: "Service Detail" },
  { key: "cta_band", label: "CTA Band" },
];

// Friendlier labels for known field keys
const FIELD_LABELS = {
  header_tagline: "Header tagline (under logo)",
  footer_blurb: "Footer description",
  header_cta_label: "Header CTA button",
  trust_badges: "Trust badges (chips)",
  hero_overline_suffix: "Hero overline suffix",
  hero_title_line_1: "Hero title — line 1",
  hero_title_line_2: "Hero title — line 2",
  hero_title_line_3_red: "Hero title — line 3 (red)",
  hero_body: "Hero body paragraph",
  hero_cta_primary: "Hero primary CTA",
  hero_cta_secondary: "Hero secondary CTA",
  intro_overline: "Intro section overline",
  intro_title_line_1: "Intro title — line 1",
  intro_title_line_2: "Intro title — line 2",
  intro_body_1: "Intro paragraph 1",
  intro_body_2: "Intro paragraph 2",
  why_overline: "Why-Choose-Us overline",
  why_title_line_1: "Why title — line 1",
  why_title_line_2: "Why title — line 2",
  why_subtitle: "Why subtitle",
  services_overline: "Services overline",
  services_title: "Services section title",
  services_view_all: "View-all button",
  specialties_overline: "Specialties overline",
  specialties_title_line_1: "Specialties title — line 1",
  specialties_title_line_2: "Specialties title — line 2",
  specialties_body: "Specialties body",
  specialties_footnote: "Specialties footnote",
  testimonials_overline: "Testimonials overline",
  testimonials_title_line_1: "Testimonials title — line 1",
  testimonials_title_line_2: "Testimonials title — line 2",
  areas_overline: "Service area overline",
  areas_title_line_1: "Service area title — line 1",
  areas_title_line_2: "Service area title — line 2",
  areas_body: "Service area body",
  hero_overline: "Hero overline",
  hero_title: "Hero title",
  empty_message: "Empty-state message",
  story_overline: "Story overline",
  story_title_line_1: "Story title — line 1",
  story_title_line_2: "Story title — line 2",
  story_body_1: "Story paragraph 1",
  story_body_2: "Story paragraph 2",
  warranty_title: "Warranty title",
  warranty_body: "Warranty body",
  warranty_overline: "Warranty overline",
  warranty_body_template: 'Warranty body (use "{service}" for service name)',
  form_overline: "Form overline",
  form_title_line_1: "Form title — line 1",
  form_title_line_2: "Form title — line 2",
  form_submit_label: "Form submit button",
  form_success_toast: "Success toast message",
  info_overline: "Contact info overline",
  back_label: "Back link label",
  overview_overline: "Overview overline",
  overview_title_line_1: "Overview title — line 1",
  overview_title_line_2: "Overview title — line 2",
  features_overline: "Features overline",
  features_title: "Features title",
  ba_overline: "Before/After overline",
  ba_title_line_1: "Before/After title — line 1",
  ba_title_line_2: "Before/After title — line 2",
  overline: "Overline",
  title_line_1: "Title — line 1",
  title_line_2: "Title — line 2",
  body: "Body",
  call_button_prefix: 'Call button prefix (e.g., "Call")',
  quote_button: "Quote button label",
  stats: "Hero stats (value + label)",
  benefits: "Why-Choose-Us cards (icon + title + body)",
};

const ICON_OPTIONS = ["ShieldCheck", "House", "Users", "MapPin", "Hammer", "Sparkle"];

function labelOf(key) {
  return FIELD_LABELS[key] || key.replace(/_/g, " ");
}

function StringField({ value, onChange, multiline, testid }) {
  const isLong = multiline || (typeof value === "string" && value.length > 80);
  if (isLong) {
    return (
      <textarea
        rows={Math.min(8, Math.max(2, Math.ceil((value || "").length / 60)))}
        className="admin-input"
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
      />
    );
  }
  return (
    <input
      type="text"
      className="admin-input"
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testid}
    />
  );
}

function StringListField({ value = [], onChange, testid }) {
  const items = value || [];
  const update = (i, v) => {
    const arr = [...items];
    arr[i] = v;
    onChange(arr);
  };
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
  const add = () => onChange([...items, ""]);
  return (
    <div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        {items.map((v, i) => (
          <div key={i} className="flex gap-2">
            <input className="admin-input flex-1" value={v} onChange={(e) => update(i, e.target.value)} data-testid={`${testid}-${i}`} />
            <button onClick={() => remove(i)} type="button" className="border border-[#333] hover:border-atc-red p-2"><X size={14} weight="bold" /></button>
          </div>
        ))}
      </div>
      <button onClick={add} type="button" className="mt-2 font-heading uppercase tracking-widest text-xs text-atc-red hover:text-white inline-flex items-center gap-1" data-testid={`${testid}-add`}>
        <Plus size={12} weight="bold" /> Add
      </button>
    </div>
  );
}

function StatsField({ value = [], onChange }) {
  const update = (i, k, v) => {
    const arr = [...value];
    arr[i] = { ...arr[i], [k]: v };
    onChange(arr);
  };
  const remove = (i) => onChange(value.filter((_, idx) => idx !== i));
  const add = () => onChange([...value, { value: "", label: "" }]);
  return (
    <div className="space-y-2">
      {value.map((s, i) => (
        <div key={i} className="grid grid-cols-[1fr_2fr_auto] gap-2 items-center">
          <input className="admin-input" placeholder="Value (e.g., 20+)" value={s.value || ""} onChange={(e) => update(i, "value", e.target.value)} data-testid={`stat-value-${i}`} />
          <input className="admin-input" placeholder="Label (e.g., Years Combined)" value={s.label || ""} onChange={(e) => update(i, "label", e.target.value)} data-testid={`stat-label-${i}`} />
          <button onClick={() => remove(i)} type="button" className="border border-[#333] hover:border-atc-red p-2"><X size={14} weight="bold" /></button>
        </div>
      ))}
      <button onClick={add} type="button" className="font-heading uppercase tracking-widest text-xs text-atc-red hover:text-white inline-flex items-center gap-1"><Plus size={12} weight="bold" /> Add stat</button>
    </div>
  );
}

function BenefitsField({ value = [], onChange }) {
  const update = (i, k, v) => {
    const arr = [...value];
    arr[i] = { ...arr[i], [k]: v };
    onChange(arr);
  };
  const remove = (i) => onChange(value.filter((_, idx) => idx !== i));
  const add = () => onChange([...value, { icon: "ShieldCheck", title: "", body: "" }]);
  return (
    <div className="space-y-3">
      {value.map((b, i) => (
        <div key={i} className="border border-[#333] p-3 space-y-2">
          <div className="grid grid-cols-[150px_1fr_auto] gap-2 items-center">
            <select className="admin-input" value={b.icon || "ShieldCheck"} onChange={(e) => update(i, "icon", e.target.value)} data-testid={`benefit-icon-${i}`}>
              {ICON_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
            <input className="admin-input" placeholder="Title" value={b.title || ""} onChange={(e) => update(i, "title", e.target.value)} data-testid={`benefit-title-${i}`} />
            <button onClick={() => remove(i)} type="button" className="border border-[#333] hover:border-atc-red p-2"><X size={14} weight="bold" /></button>
          </div>
          <textarea rows={2} className="admin-input" placeholder="Body" value={b.body || ""} onChange={(e) => update(i, "body", e.target.value)} data-testid={`benefit-body-${i}`} />
        </div>
      ))}
      <button onClick={add} type="button" className="font-heading uppercase tracking-widest text-xs text-atc-red hover:text-white inline-flex items-center gap-1"><Plus size={12} weight="bold" /> Add card</button>
    </div>
  );
}

function FieldRow({ fieldKey, value, onChange }) {
  const label = labelOf(fieldKey);

  // Specialized renderers
  if (fieldKey === "stats") {
    return (
      <div className="border-t border-[#2a2a2a] pt-4">
        <div className="admin-label">{label}</div>
        <StatsField value={value} onChange={onChange} />
      </div>
    );
  }
  if (fieldKey === "benefits") {
    return (
      <div className="border-t border-[#2a2a2a] pt-4">
        <div className="admin-label">{label}</div>
        <BenefitsField value={value} onChange={onChange} />
      </div>
    );
  }
  if (Array.isArray(value)) {
    return (
      <div>
        <div className="admin-label">{label}</div>
        <StringListField value={value} onChange={onChange} testid={`field-${fieldKey}`} />
      </div>
    );
  }
  return (
    <div>
      <div className="admin-label">{label}</div>
      <StringField value={value} onChange={onChange} testid={`field-${fieldKey}`} />
    </div>
  );
}

export default function AdminContent() {
  const [content, setContent] = useState(null);
  const [tab, setTab] = useState("global");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const r = await api.get("/content");
    setContent(r.data);
  };
  useEffect(() => { load(); }, []);

  const sectionKeys = useMemo(() => {
    if (!content) return [];
    return Object.keys(content[tab] || {});
  }, [content, tab]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/content", content);
      toast.success("Saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    if (!window.confirm("Reset ALL page content to factory defaults? This cannot be undone.")) return;
    try {
      await api.post("/content/reset");
      await load();
      toast.success("Restored defaults");
    } catch (e) {
      toast.error("Reset failed");
    }
  };

  const updateField = (key, val) => {
    setContent({
      ...content,
      [tab]: { ...content[tab], [key]: val },
    });
  };

  if (!content) return <div className="p-10 text-white/40">Loading…</div>;

  return (
    <div className="p-8 md:p-10" data-testid="admin-content">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
        <div>
          <div className="overline mb-2">Copy</div>
          <h1 className="font-display text-5xl uppercase text-white leading-none">Page Content</h1>
          <p className="text-white/60 mt-2 text-sm">Every text field on the public site, organized by page.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={reset} className="btn-ghost-white !text-white !border-[#333] hover:!border-atc-red" data-testid="reset-content-btn">
            <ArrowCounterClockwise size={16} weight="bold" /> Reset to defaults
          </button>
          <button onClick={save} disabled={saving} className="btn-primary disabled:opacity-50" data-testid="save-content-btn">
            <FloppyDisk size={18} weight="bold" /> {saving ? "Saving…" : "Save changes"}
          </button>
        </div>
      </div>

      <div className="flex flex-wrap gap-1 mb-6 border-b border-[#2a2a2a]" data-testid="content-tabs">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            data-testid={`tab-${t.key}`}
            className={`px-4 py-3 font-heading uppercase tracking-widest text-xs transition-colors border-b-2 -mb-px ${
              tab === t.key
                ? "text-white border-atc-red"
                : "text-white/50 border-transparent hover:text-white"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="admin-card space-y-5 max-w-4xl">
        {sectionKeys.length === 0 ? (
          <p className="text-white/50 text-sm">Nothing in this section.</p>
        ) : (
          sectionKeys.map((k) => (
            <FieldRow
              key={k}
              fieldKey={k}
              value={content[tab][k]}
              onChange={(v) => updateField(k, v)}
            />
          ))
        )}
      </div>

      <div className="mt-6 flex gap-2 max-w-4xl justify-end">
        <button onClick={save} disabled={saving} className="btn-primary disabled:opacity-50" data-testid="save-content-btn-bottom">
          <FloppyDisk size={18} weight="bold" /> {saving ? "Saving…" : "Save changes"}
        </button>
      </div>
    </div>
  );
}
